// Reuse merchant-authored, localized content. Never generate product claims.
const clean = (text) => (text || '').replace(/\s+/g, ' ').trim();
const heading = (node) => /^H[2-4]$/.test(node?.tagName || '');
const benefitHeading = /benefit|why (choose|you)|key feature|предимств|защо|характеристик|основни детайли|product details|specification/i;
const safetyHeading = /warning|caution|storage|ingredient|предупрежд|състав|съхранение/i;

export function descriptionParts(root) {
  if (root.dataset?.enhanced) return [...root.querySelectorAll(':scope > .vp-guide-disclosure')].map((details) => ({ title: clean(details.querySelector('summary').textContent), nodes: [...details.querySelector('.vp-guide-disclosure__body').childNodes] }));
  const nodes = [];
  const walk = (node) => {
    if (/^(DIV|SECTION|ARTICLE)$/.test(node.tagName || '') && node.querySelector('h2,h3,h4')) {
      [...node.childNodes].forEach(walk);
    } else nodes.push(node);
  };
  [...root.childNodes].forEach(walk);
  const parts = [{ title: '', nodes: [] }];
  for (const node of nodes) {
    if (heading(node)) parts.push({ title: clean(node.textContent), heading: node, nodes: [] });
    else parts.at(-1).nodes.push(node);
  }
  return parts.filter((part) => part.title || part.nodes.some((n) => clean(n.textContent) || n.nodeType === 1));
}

export function productHighlights(root) {
  const lines = [];
  for (const part of descriptionParts(root)) {
    if (!benefitHeading.test(part.title) || safetyHeading.test(part.title)) continue;
    for (const node of part.nodes) {
      const items = node.querySelectorAll ? [...node.querySelectorAll('li')] : [];
      for (const item of items) {
        const text = clean(item.textContent);
        if (text.length >= 8 && text.length <= 140 && !lines.includes(text)) lines.push(text);
      }
    }
  }
  return lines.slice(0, 3);
}

export function comparisonFacts(root, title = '') {
  const facts = new Map();
  const patterns = [
    ['hold', /^(?:(?:maximum|максимална)\s+)?(?:hold|fixation|фиксация|сила на фиксация|задържане)\s*(?:strength|level|ниво)?\s*[:–—-]\s*(.+)$/i],
    ['shine', /^(?:(?:maximum|максимален)\s+)?(?:shine|блясък)\s*(?:level|ниво)?\s*[:–—-]\s*(.+)$/i],
    ['runtime', /^(?:runtime|run time|working time|battery life|време на работа|работно време|автономна работа)\s*[:–—-]\s*(.+)$/i],
    ['speed', /^(?:motor speed|speed|скорост на мотора|обороти)\s*[:–—-]\s*(.+)$/i],
    ['size', /^(?:size|volume|capacity|обем|разфасовка|количество)\s*[:–—-]\s*(.+)$/i],
  ];
  for (const el of root.querySelectorAll('li, tr, p')) {
    const text = clean(el.textContent);
    if (text.length > 140) continue;
    for (const [key, pattern] of patterns) {
      const match = text.match(pattern);
      if (match && !facts.has(key)) facts.set(key, match[1]);
    }
  }
  // A single size in the title is unambiguous. Multiple sizes remain in the native variant selector.
  const sizes = title.match(/\b\d+(?:[.,]\d+)?\s*(?:ml|мл|kg|кг|g|гр)\b/gi) || [];
  if (!facts.has('size') && sizes.length === 1) facts.set('size', sizes[0]);
  if (facts.size < 2) {
    for (const [index, text] of productHighlights(root).slice(0, 2).entries()) {
      if (![...facts.values()].some((value) => text.includes(value))) facts.set(`detail${index}`, text);
    }
  }
  return facts;
}

export function enhanceDescription(root, overview) {
  if (root.dataset.enhanced) return;
  const parts = descriptionParts(root);
  if (parts.filter((p) => p.title).length < 2) return;
  const fragment = document.createDocumentFragment();
  for (const [index, part] of parts.entries()) {
    const details = document.createElement('details');
    details.className = 'vp-guide-disclosure';
    details.open = index === 0;
    const summary = document.createElement('summary');
    // Move the real heading, retaining its text and ID for existing deep links.
    if (part.heading) summary.append(part.heading);
    else {
      const h = document.createElement('h3'); h.textContent = overview; summary.append(h);
    }
    const body = document.createElement('div');
    body.className = 'vp-guide-disclosure__body';
    body.append(...part.nodes);
    details.append(summary, body);
    fragment.append(details);
  }
  root.replaceChildren(fragment);
  root.dataset.enhanced = 'true';
}

class VivaProductSummary extends HTMLElement {
  connectedCallback() {
    const root = document.querySelector('[data-viva-description]');
    if (!root) return;
    const lines = productHighlights(root);
    const list = this.querySelector('[data-product-highlights]');
    if (!list) return;
    list.replaceChildren();
    for (const text of lines) {
      const li = document.createElement('li'); li.textContent = text; list.append(li);
    }
    if (!lines.length) {
      const paragraph = [...root.querySelectorAll('p')].find((p) => {
        const text = clean(p.textContent); return text.length >= 35 && text.length <= 600;
      });
      if (paragraph) {
        const intro = this.querySelector('[data-product-intro]');
        intro.textContent = clean(paragraph.textContent); intro.hidden = false; list.hidden = true;
      } else return;
    }
    this.hidden = false;
  }
}
class VivaProductGuide extends HTMLElement {
  connectedCallback() {
    const root = this.querySelector('[data-viva-description]');
    if (root) enhanceDescription(root, this.dataset.overview);
  }
}
class VivaComparisonFacts extends HTMLElement {
  connectedCallback() {
    const source = this.querySelector('template');
    if (!source || this.querySelector('dl')) return;
    const facts = comparisonFacts(source.content, this.dataset.title);
    if (!facts.size) return;
    const dl = document.createElement('dl');
    for (const [key, value] of [...facts].slice(0, 3)) {
      const row = document.createElement('div');
      const dt = document.createElement('dt'); dt.textContent = this.dataset[`${key}Label`] || this.dataset.detailLabel;
      const dd = document.createElement('dd'); dd.textContent = value;
      row.append(dt, dd); dl.append(row);
    }
    this.append(dl); this.hidden = false;
  }
}
// Upgrade summaries before converting the source into disclosures.
if (!customElements.get('viva-product-summary')) customElements.define('viva-product-summary', VivaProductSummary);
if (!customElements.get('viva-product-guide')) customElements.define('viva-product-guide', VivaProductGuide);
if (!customElements.get('viva-comparison-facts')) customElements.define('viva-comparison-facts', VivaComparisonFacts);
